import React, { useContext, useCallback } from 'react';
import { AppContext } from '../context/AppContext';
import SearchModal from './SearchModal';

/**
 * Plot search modal with lazy loading
 * @param {boolean} open - Whether the modal is open
 * @param {function} onClose - Callback when modal is closed
 * @param {function} onSelect - Callback when a plot is selected
 * @param {number} selectedId - Currently selected plot ID (for highlighting)
 * @param {array} excludeIds - Array of plot IDs to exclude from results
 */
const PlotSearchModal = ({
  open,
  onClose,
  onSelect,
  selectedId = null,
  excludeIds = []
}) => {
  const { token } = useContext(AppContext);

  const fetchPlots = useCallback(async (searchTerm, page, limit) => {
    try {
      // Fetch plots and their contacts
      const [plotsRes, plotContactsRes] = await Promise.all([
        fetch('/api/plots', {
          headers: token ? { Authorization: token } : {}
        }),
        fetch('/api/plot-contacts', {
          headers: token ? { Authorization: token } : {}
        })
      ]);

      if (!plotsRes.ok) {
        throw new Error('Failed to fetch plots');
      }

      let plots = await plotsRes.json();
      const plotContacts = plotContactsRes.ok ? await plotContactsRes.json() : [];

      // Create a plot contacts lookup
      const plotContactsMap = {};
      plotContacts.forEach(pc => {
        if (!plotContactsMap[pc.plotId]) {
          plotContactsMap[pc.plotId] = [];
        }
        plotContactsMap[pc.plotId].push(pc);
      });

      // Enhance plots with contact count
      plots = plots.map(plot => ({
        ...plot,
        contacts: plotContactsMap[plot.fid] || [],
        contactCount: (plotContactsMap[plot.fid] || []).length
      }));

      // Filter out excluded plots
      if (excludeIds.length > 0) {
        plots = plots.filter(p => !excludeIds.includes(p.fid));
      }

      // Client-side search filtering
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        plots = plots.filter(plot => {
          const alias = (plot.alias || '').toLowerCase();
          const section = (plot.section || '').toLowerCase();
          const size = (plot.size || '').toLowerCase();
          // Search in contact names too
          const contactNames = plot.contacts
            .map(pc => {
              const contact = pc.Contact;
              return contact ? `${contact.firstName || ''} ${contact.lastName || ''}`.toLowerCase() : '';
            })
            .join(' ');

          return (
            alias.includes(term) ||
            section.includes(term) ||
            size.includes(term) ||
            contactNames.includes(term)
          );
        });
      }

      // Sort by alias
      plots.sort((a, b) => {
        const aliasA = (a.alias || '').toLowerCase();
        const aliasB = (b.alias || '').toLowerCase();
        return aliasA.localeCompare(aliasB);
      });

      // Pagination
      const start = (page - 1) * limit;
      const paginatedPlots = plots.slice(start, start + limit);

      return {
        items: paginatedPlots,
        total: plots.length
      };
    } catch (error) {
      console.error('Error fetching plots:', error);
      return { items: [], total: 0 };
    }
  }, [token, excludeIds]);

  const columns = [
    {
      key: 'alias',
      label: 'Plot',
      render: (plot) => (
        <div>
          <div className="font-medium text-gray-900">
            {plot.alias || 'Unnamed'}
          </div>
          {plot.section && (
            <div className="text-sm text-gray-500">
              Section: {plot.section}
            </div>
          )}
        </div>
      )
    },
    {
      key: 'size',
      label: 'Size',
      render: (plot) => (
        <span className="text-gray-600">{plot.size || '-'}</span>
      )
    },
    {
      key: 'contacts',
      label: 'Contacts',
      render: (plot) => (
        <div>
          {plot.contactCount > 0 ? (
            <div>
              <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
                {plot.contactCount} contact{plot.contactCount !== 1 ? 's' : ''}
              </span>
              {plot.contacts.slice(0, 2).map((pc, idx) => (
                <div key={idx} className="text-xs text-gray-500 mt-1">
                  {pc.Contact?.firstName} {pc.Contact?.lastName}
                  <span className="text-gray-400 ml-1">({pc.relationshipType})</span>
                </div>
              ))}
              {plot.contactCount > 2 && (
                <div className="text-xs text-gray-400 mt-1">
                  +{plot.contactCount - 2} more
                </div>
              )}
            </div>
          ) : (
            <span className="text-gray-400 text-sm">No contacts</span>
          )}
        </div>
      )
    },
    {
      key: 'status',
      label: 'Status',
      render: (plot) => {
        const hasInterred = plot.contacts.some(pc => pc.relationshipType === 'interred');
        const hasContacts = plot.contactCount > 0;

        let statusText = 'Available';
        let statusClass = 'bg-gray-100 text-gray-700';

        if (hasInterred) {
          statusText = 'Occupied';
          statusClass = 'bg-blue-100 text-blue-700';
        } else if (hasContacts) {
          statusText = 'Reserved';
          statusClass = 'bg-green-100 text-green-700';
        }

        return (
          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${statusClass}`}>
            {statusText}
          </span>
        );
      }
    }
  ];

  return (
    <SearchModal
      open={open}
      onClose={onClose}
      onSelect={onSelect}
      title="Select Plot"
      searchPlaceholder="Search by alias, section, or contact name..."
      fetchItems={fetchPlots}
      columns={columns}
      getItemId={(plot) => plot.fid}
      emptyMessage="No plots found"
      selectedId={selectedId}
    />
  );
};

export default PlotSearchModal;
